import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAccessibility } from "@/context/AccessibilityContext";
import { AccessibilityToolbar } from "@/components/AccessibilityToolbar";
import { StickyMobileFooter } from "@/components/StickyMobileFooter";
import { StatusBadge } from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";
import { toast } from "sonner";
import {
  Palette, Type as TypeIcon, Accessibility, Layout as LayoutIcon, Home, Building2, BedDouble,
  ShieldCheck, PackageOpen, Send, Mic, Video, PhoneOff, Users, Star, ArrowRight, Sun,
  MessageCircle, TrendingUp, CalendarHeart, Smartphone, Boxes, FileText, Lock,
} from "lucide-react";

const IMG = {
  road: "https://images.unsplash.com/photo-1613419380964-ad761bb26611?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  openRoad: "https://images.unsplash.com/photo-1585552622005-f6c9228f6b68?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  couple: "https://images.unsplash.com/photo-1758686253692-2d6921de12dd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  shore: "https://images.unsplash.com/photo-1782179119846-ce6a1c898ef5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  aerial: "https://images.unsplash.com/photo-1504203328729-b937e8e102f2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  truck: "https://images.unsplash.com/photo-1605504835488-e8c6d37beb43?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
};

const PALETTE = [
  { name: "Navy / Blue-Green", hex: "#1C4358", fg: "#fff", role: "Structure, nav, headings" },
  { name: "Mama Red", hex: "#9C1E22", fg: "#fff", role: "Primary CTAs" },
  { name: "Warm Cream", hex: "#F6EDD9", fg: "#1C4358", role: "Warm section backgrounds" },
  { name: "Soft Sky", hex: "#9BB9CE", fg: "#1C4358", role: "Info & calm accents" },
  { name: "Sun-Glow Gold", hex: "#E7A83A", fg: "#1C4358", role: "Highlights & badges" },
  { name: "Khaki", hex: "#C4C89B", fg: "#1C4358", role: "Soft accents" },
  { name: "Success Green", hex: "#2F7D5A", fg: "#fff", role: "Positive states" },
  { name: "Warning Amber", hex: "#B7791F", fg: "#fff", role: "Warnings" },
  { name: "Charcoal", hex: "#252221", fg: "#fff", role: "Body text" },
];

const LANES = [
  { icon: Building2, label: "Property Sale", desc: "List & sell the family home" },
  { icon: BedDouble, label: "Senior Placement", desc: "Find the right community" },
  { icon: ShieldCheck, label: "Home Safety", desc: "Age-in-place assessments" },
  { icon: Home, label: "Move Mama In", desc: "Multi-gen home transitions" },
  { icon: PackageOpen, label: "Downsizing", desc: "Sort, pack & simplify" },
  { icon: Users, label: "Trusted Partners", desc: "Vetted local vendors" },
];

const PIE = [
  { name: "Leads", value: 34, color: "#9BB9CE" },
  { name: "Consultations", value: 22, color: "#C4C89B" },
  { name: "Active Cases", value: 28, color: "#1C4358" },
  { name: "Closed / Won", value: 16, color: "#9C1E22" },
];

const Section = ({ id, icon: Icon, title, desc, children }) => (
  <section id={id} className="scroll-mt-24" data-testid={`ds-section-${id}`}>
    <div className="flex items-center gap-2.5">
      {Icon && <Icon size={18} className="text-[#9C1E22]" />}
      <h2 className="font-display text-xl font-bold text-[#1C4358]">{title}</h2>
    </div>
    {desc && <p className="mt-1 text-sm text-muted-foreground max-w-2xl">{desc}</p>}
    <div className="mt-4">{children}</div>
  </section>
);

const ChatBubble = ({ side, children }) => (
  <div className={`flex ${side === "out" ? "justify-end" : "justify-start"}`}>
    <div className={`max-w-[78%] px-3.5 py-2 text-sm ${side === "out" ? "mmm-chat-out" : "mmm-chat-in"}`}>{children}</div>
  </div>
);

export default function DesignSystem() {
  const a11y = useAccessibility();
  const [accent, setAccent] = useState("#9C1E22");
  const [brand, setBrand] = useState({ org_name: "", tagline: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings")
      .then((r) => setBrand({ org_name: r.data.org_name || "", tagline: r.data.tagline || "" }))
      .catch(() => {});
  }, []);

  const applyAccent = (v) => {
    setAccent(v);
    document.documentElement.style.setProperty("--mmm-red", v);
  };
  const saveBrand = async () => {
    setSaving(true);
    try { await api.put("/settings", brand); toast.success("Brand settings saved"); }
    catch { toast.error("Could not save brand settings"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-10 pb-10" data-testid="design-system-page">
      {/* Page header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Palette size={22} className="text-[#9C1E22]" />
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#252221]">Design System</h1>
            <Badge className="bg-[color:var(--mmm-gold)] text-[#1C4358] hover:bg-[color:var(--mmm-gold)]">Super Admin</Badge>
          </div>
          <p className="mt-1 text-sm text-muted-foreground max-w-2xl">
            The single source of truth for MovingMyMama. Every public page, portal, admin screen, and
            franchise sub-site uses these tokens and components. Only Super Admins can change global brand elements.
          </p>
        </div>
        <AccessibilityToolbar triggerClassName="border border-[#E6EDF2] bg-white" />
      </div>

      {/* Brand controls (Super Admin) */}
      <Section id="brand" icon={Lock} title="Global Brand Controls"
        desc="Super Admin only. Adjust the organization identity and preview the primary accent across the system.">
        <Card className="p-5 border-[#E6EDF2] grid md:grid-cols-3 gap-5" style={{ boxShadow: "var(--mmm-shadow-sm)" }}>
          <div className="space-y-1.5">
            <Label className="text-[13px] font-medium">Organization name</Label>
            <Input value={brand.org_name} onChange={(e) => setBrand((b) => ({ ...b, org_name: e.target.value }))} data-testid="ds-brand-org" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[13px] font-medium">Tagline</Label>
            <Input value={brand.tagline} onChange={(e) => setBrand((b) => ({ ...b, tagline: e.target.value }))} data-testid="ds-brand-tagline" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[13px] font-medium">Primary accent <span className="text-muted-foreground font-normal">(session preview)</span></Label>
            <div className="flex items-center gap-2">
              <input type="color" value={accent} onChange={(e) => applyAccent(e.target.value)} data-testid="ds-brand-accent"
                className="h-10 w-14 rounded-lg border border-[#E6EDF2] bg-white p-1" aria-label="Primary accent color" />
              <code className="text-sm text-[#1C4358]">{accent}</code>
            </div>
          </div>
          <div className="md:col-span-3">
            <Button onClick={saveBrand} disabled={saving} data-testid="ds-brand-save" className="text-white" style={{ backgroundColor: "var(--mmm-red)" }}>
              {saving ? "Saving…" : "Save brand settings"}
            </Button>
          </div>
        </Card>
      </Section>

      {/* Accessibility */}
      <Section id="accessibility" icon={Accessibility} title="Accessibility (Senior-Friendly)"
        desc="A visible text-size toggle (Standard / Large / Extra Large), high-readability mode, and reduced-motion mode are available on every screen. Large tap targets (≥44px), high contrast, and clear focus rings are enforced globally.">
        <Card className="p-5 border-[#E6EDF2] flex flex-wrap items-center gap-6" style={{ boxShadow: "var(--mmm-shadow-sm)" }}>
          <AccessibilityToolbar triggerClassName="border border-[#E6EDF2] bg-white" />
          <div className="text-sm text-muted-foreground">
            <div>Current text size: <b className="text-[#1C4358]">{a11y?.textSize}</b></div>
            <div>High readability: <b className="text-[#1C4358]">{a11y?.highReadability ? "On" : "Off"}</b> · Reduced motion: <b className="text-[#1C4358]">{a11y?.reducedMotion ? "On" : "Off"}</b></div>
          </div>
          <div className="flex flex-wrap gap-2">
            {["standard", "large", "xl"].map((s) => (
              <Button key={s} variant="outline" onClick={() => a11y?.setTextSize(s)} data-testid={`ds-a11y-set-${s}`}
                className={a11y?.textSize === s ? "border-[#1C4358] text-[#1C4358]" : ""}>
                {s === "standard" ? "Standard" : s === "large" ? "Large" : "Extra Large"}
              </Button>
            ))}
          </div>
        </Card>
      </Section>

      {/* Colors */}
      <Section id="palette" icon={Palette} title="Color Palette"
        desc="Drawn from the vintage-truck logo: deep navy/blue-green, Mama Red, warm cream, soft sky, and a small sun-glow gold highlight.">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {PALETTE.map((c) => (
            <div key={c.name} className="rounded-[var(--radius)] overflow-hidden border border-[#E6EDF2]" data-testid={`ds-swatch-${c.hex.replace('#','')}`}>
              <div className="h-20 flex items-end p-2" style={{ backgroundColor: c.hex, color: c.fg }}>
                <span className="text-xs font-semibold">{c.hex}</span>
              </div>
              <div className="p-2.5 bg-white">
                <div className="text-sm font-semibold text-[#252221]">{c.name}</div>
                <div className="text-xs text-muted-foreground">{c.role}</div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Typography */}
      <Section id="typography" icon={TypeIcon} title="Typography"
        desc="Space Grotesk for headings, Work Sans for body. The scale below responds live to the text-size toggle.">
        <Card className="p-6 border-[#E6EDF2] space-y-3" style={{ boxShadow: "var(--mmm-shadow-sm)" }}>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#1C4358]">Helping families move Mama with care</h1>
          <h2 className="text-base md:text-lg font-semibold text-[#252221]">A calm, guided next step — H2 subheading</h2>
          <h3 className="text-lg font-semibold text-[#252221]">Section heading — H3</h3>
          <p className="text-sm md:text-base leading-7 text-[#252221] max-w-2xl">Body copy stays readable and roomy. We keep line length comfortable and never drop below 14px on mobile, so older eyes can read without strain.</p>
          <p className="text-sm text-muted-foreground">Small / muted supporting text.</p>
        </Card>
      </Section>

      {/* Buttons */}
      <Section id="buttons" icon={ArrowRight} title="Buttons & CTAs"
        desc="Rounded, high-contrast, with clear press and focus states. Mama Red is reserved for positive primary actions.">
        <Card className="p-6 border-[#E6EDF2] flex flex-wrap items-center gap-3" style={{ boxShadow: "var(--mmm-shadow-sm)" }}>
          <Button className="text-white h-11 px-5 text-base" style={{ backgroundColor: "var(--mmm-red)" }} data-testid="ds-btn-primary">Book Free Consultation</Button>
          <Button variant="outline" className="h-11 px-5 text-[#1C4358] border-[#E6EDF2]" data-testid="ds-btn-secondary">Learn more</Button>
          <Button variant="ghost" className="h-11 px-5 text-[#1C4358]" data-testid="ds-btn-ghost">Skip for now</Button>
          <Button className="h-11 px-5 text-white bg-[#7A0F12] hover:bg-[#650C0F]" data-testid="ds-btn-destructive">Delete</Button>
          <Button className="h-11 px-5 text-[#1C4358]" style={{ backgroundColor: "var(--mmm-gold)" }} data-testid="ds-btn-gold"><Sun size={16} className="mr-1.5" /> Highlight</Button>
        </Card>
      </Section>

      {/* Forms */}
      <Section id="forms" icon={FileText} title="Forms & Inputs"
        desc="Always-visible labels, large fields, and thumb-friendly controls for older users.">
        <Card className="p-6 border-[#E6EDF2] grid md:grid-cols-2 gap-5 max-w-3xl" style={{ boxShadow: "var(--mmm-shadow-sm)" }}>
          <div className="space-y-1.5"><Label className="text-[13px] font-medium">Full name</Label><Input placeholder="Jane Smith" className="h-11" data-testid="ds-input-name" /></div>
          <div className="space-y-1.5"><Label className="text-[13px] font-medium">Phone</Label><Input placeholder="(816) 555-0100" className="h-11" data-testid="ds-input-phone" /></div>
          <div className="space-y-1.5 md:col-span-2"><Label className="text-[13px] font-medium">How can we help?</Label><Textarea rows={3} placeholder="Tell us a little about your situation…" data-testid="ds-input-message" /></div>
          <div className="space-y-1.5"><Label className="text-[13px] font-medium">Service needed</Label>
            <Select><SelectTrigger className="h-11" data-testid="ds-select-service"><SelectValue placeholder="Choose a service" /></SelectTrigger>
              <SelectContent><SelectItem value="sale">Property sale</SelectItem><SelectItem value="placement">Senior placement</SelectItem><SelectItem value="downsizing">Downsizing</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-6 pt-6">
            <label className="flex items-center gap-2 text-sm"><Checkbox data-testid="ds-check-consent" /> Email me updates</label>
            <label className="flex items-center gap-2 text-sm">Text OK <Switch data-testid="ds-switch-sms" /></label>
          </div>
        </Card>
      </Section>

      {/* Hero */}
      <Section id="hero" icon={Sun} title="Hero Section"
        desc="Journey imagery (sunrise, open road, the next step) with a readable overlay. This is the template public and landing pages inherit.">
        <div className="relative overflow-hidden rounded-[var(--radius)] border border-[#E6EDF2]" style={{ minHeight: 320 }} data-testid="ds-hero">
          <img src={IMG.road} alt="Sunrise over an open country road" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 mmm-hero-overlay" />
          <div className="mmm-grain absolute inset-0 opacity-60" />
          <div className="relative p-6 sm:p-10 max-w-xl text-white">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full" style={{ backgroundColor: "rgba(231,168,58,0.9)", color: "#1C4358" }}><Star size={12} /> Trusted by 500+ families</span>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold leading-tight">The next step for Mama, made simple and kind.</h2>
            <p className="mt-3 text-base text-white/90">From selling the family home to finding the right community — one caring team guides every mile of the journey.</p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Button className="h-11 px-5 text-white text-base" style={{ backgroundColor: "var(--mmm-red)" }} data-testid="ds-hero-cta">Book a Free Consultation</Button>
              <Button className="h-11 px-5 bg-white text-[#1C4358] hover:bg-white/90" data-testid="ds-hero-secondary">Call (816) 555-0100</Button>
            </div>
          </div>
        </div>
      </Section>

      {/* Service tiles */}
      <Section id="lanes" icon={Boxes} title="Service-Lane Tiles"
        desc="Each core service is a warm, tappable tile with a clear icon and one-line promise.">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {LANES.map((l) => (
            <Card key={l.label} className="mmm-tile p-5 border-[#E6EDF2] cursor-pointer" style={{ boxShadow: "var(--mmm-shadow-sm)" }} data-testid={`ds-lane-${l.label.toLowerCase().replace(/\s+/g,'-')}`}>
              <div className="h-11 w-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: "rgba(155,185,206,0.25)" }}>
                <l.icon size={22} className="text-[#1C4358]" />
              </div>
              <div className="mt-3 font-display font-semibold text-[#252221]">{l.label}</div>
              <div className="text-sm text-muted-foreground">{l.desc}</div>
              <div className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-[#9C1E22]">Explore <ArrowRight size={14} /></div>
            </Card>
          ))}
        </div>
      </Section>

      {/* Reverse landing template */}
      <Section id="landing" icon={LayoutIcon} title="Reverse Landing Page Template"
        desc="A conversion-first layout: value proposition on the left, a short lead-capture form on the right.">
        <Card className="overflow-hidden border-[#E6EDF2] grid md:grid-cols-2" style={{ boxShadow: "var(--mmm-shadow-sm)" }} data-testid="ds-landing">
          <div className="relative p-6 sm:p-8 text-white" style={{ backgroundColor: "var(--mmm-navy)" }}>
            <div className="mmm-grain absolute inset-0 opacity-40" />
            <div className="relative">
              <h3 className="font-display text-2xl font-bold">Get a free, no-pressure plan for Mama’s move</h3>
              <ul className="mt-4 space-y-2 text-sm text-white/90">
                {["One caring coordinator, start to finish", "Vetted local movers, agents & communities", "No cost to start — we plan around Mama"].map((t) => (
                  <li key={t} className="flex items-start gap-2"><Star size={14} className="mt-0.5 text-[color:var(--mmm-gold)]" /> {t}</li>
                ))}
              </ul>
              <div className="mt-6 flex items-center gap-3">
                <img src={IMG.couple} alt="Happy senior couple" className="h-14 w-14 rounded-full object-cover ring-2 ring-white/30" />
                <p className="text-sm text-white/85">“They made a hard year feel calm.” — The Millers</p>
              </div>
            </div>
          </div>
          <div className="p-6 sm:p-8 bg-white space-y-3">
            <div className="space-y-1.5"><Label className="text-[13px] font-medium">Your name</Label><Input className="h-11" data-testid="ds-landing-name" /></div>
            <div className="space-y-1.5"><Label className="text-[13px] font-medium">Phone or email</Label><Input className="h-11" data-testid="ds-landing-contact" /></div>
            <Button className="w-full h-12 text-white text-base" style={{ backgroundColor: "var(--mmm-red)" }} data-testid="ds-landing-submit">Get my free plan</Button>
            <p className="text-xs text-muted-foreground text-center">No spam. We’ll only reach out about your family’s move.</p>
          </div>
        </Card>
      </Section>

      {/* Admin dashboard cards + table + report */}
      <Section id="admin" icon={TrendingUp} title="Admin Dashboard Cards, Tables & Reports"
        desc="Airy KPI cards, scannable data tables, and calm report charts using the brand series colors.">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { label: "Active Cases", value: "128", tint: "rgba(28,67,88,0.08)", color: "#1C4358" },
            { label: "Consultations", value: "42", tint: "rgba(155,185,206,0.25)", color: "#1C4358" },
            { label: "Revenue (MTD)", value: "$86k", tint: "rgba(47,125,90,0.14)", color: "#2F7D5A" },
            { label: "New Leads", value: "37", tint: "rgba(231,168,58,0.18)", color: "#9A6516" },
          ].map((k) => (
            <Card key={k.label} className="p-4 border-[#E6EDF2]" style={{ boxShadow: "var(--mmm-shadow-sm)" }} data-testid={`ds-kpi-${k.label.toLowerCase().replace(/[^a-z]/g,'')}`}>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{k.label}</div>
              <div className="mt-1 font-display text-2xl font-bold tabular-nums" style={{ color: k.color }}>{k.value}</div>
            </Card>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-4 mt-4">
          <Card className="lg:col-span-2 border-[#E6EDF2] overflow-hidden" style={{ boxShadow: "var(--mmm-shadow-sm)" }}>
            <div className="overflow-x-auto mmm-scroll">
              <Table data-testid="ds-table">
                <TableHeader><TableRow className="hover:bg-transparent">
                  <TableHead className="text-xs font-semibold text-[#1C4358]/80">Family</TableHead>
                  <TableHead className="text-xs font-semibold text-[#1C4358]/80">Service</TableHead>
                  <TableHead className="text-xs font-semibold text-[#1C4358]/80">Status</TableHead>
                  <TableHead className="text-xs font-semibold text-[#1C4358]/80">Value</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {[
                    { f: "Smith Family", s: "Property Sale", st: "in_progress", v: "$18,400" },
                    { f: "Nguyen Family", s: "Placement", st: "consultation_booked", v: "$3,200" },
                    { f: "Garcia Family", s: "Downsizing", st: "completed", v: "$6,750" },
                    { f: "Baker Family", s: "Home Safety", st: "new", v: "$1,100" },
                  ].map((r) => (
                    <TableRow key={r.f} className="hover:bg-[rgba(155,185,206,0.10)]">
                      <TableCell className="font-medium text-[#252221]">{r.f}</TableCell>
                      <TableCell className="text-sm">{r.s}</TableCell>
                      <TableCell><StatusBadge value={r.st} /></TableCell>
                      <TableCell className="text-sm tabular-nums text-[#252221]">{r.v}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>

          <Card className="p-4 border-[#E6EDF2]" style={{ boxShadow: "var(--mmm-shadow-sm)" }} data-testid="ds-report-card">
            <div className="font-display font-semibold text-[#1C4358] mb-1">Pipeline distribution</div>
            <div style={{ height: 220, minWidth: 0 }}>
              <ResponsiveContainer width="99%" height={220}>
                <PieChart>
                  <Pie data={PIE} dataKey="value" nameKey="name" innerRadius={45} outerRadius={78} paddingAngle={2}>
                    {PIE.map((e) => <Cell key={e.name} fill={e.color} />)}
                  </Pie>
                  <Tooltip />
                  <Legend verticalAlign="bottom" height={28} wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>
      </Section>

      {/* Chat + Portal + Meeting */}
      <Section id="conversations" icon={MessageCircle} title="Chat, Portal Panels & Meeting Room"
        desc="Warm conversation surfaces for families and partners, plus a simple video meeting layout.">
        <div className="grid lg:grid-cols-3 gap-4">
          {/* Chat window */}
          <Card className="border-[#E6EDF2] overflow-hidden flex flex-col" style={{ boxShadow: "var(--mmm-shadow-sm)", minHeight: 300 }} data-testid="ds-chat-window">
            <div className="flex items-center gap-2.5 px-4 py-3 text-white" style={{ backgroundColor: "var(--mmm-navy)" }}>
              <span className="relative flex h-8 w-8 items-center justify-center rounded-full bg-white/15">
                <Users size={16} />
                <span className="absolute -right-0.5 -bottom-0.5 h-2.5 w-2.5 rounded-full bg-[#2F7D5A] ring-2 ring-[#1C4358]" />
              </span>
              <div className="leading-tight"><div className="text-sm font-semibold">Care Coordinator</div><div className="text-[11px] text-white/70">Online</div></div>
            </div>
            <div className="flex-1 p-3 space-y-2 bg-[#F7FAFC]">
              <ChatBubble side="in">Hi! How can we help with Mama’s move today?</ChatBubble>
              <ChatBubble side="out">We’d like to sell the home and find assisted living.</ChatBubble>
              <ChatBubble side="in">Wonderful — I’ll set up a free consultation. 🌅</ChatBubble>
            </div>
            <div className="p-2.5 border-t border-[#E6EDF2] flex items-center gap-2 bg-white">
              <Input placeholder="Type a message…" className="h-10" data-testid="ds-chat-input" />
              <Button className="h-10 w-10 p-0 text-white" style={{ backgroundColor: "var(--mmm-red)" }} data-testid="ds-chat-send"><Send size={16} /></Button>
            </div>
          </Card>

          {/* Portal panel */}
          <Card className="p-5 border-[#E6EDF2]" style={{ boxShadow: "var(--mmm-shadow-sm)" }} data-testid="ds-portal-panel">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Family Portal</div>
            <div className="mt-1 font-display text-lg font-bold text-[#1C4358]">Welcome back, Jane</div>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <div className="rounded-lg p-3" style={{ backgroundColor: "rgba(155,185,206,0.18)" }}><div className="text-xl font-bold text-[#1C4358] tabular-nums">3</div><div className="text-xs text-muted-foreground">Open tasks</div></div>
              <div className="rounded-lg p-3" style={{ backgroundColor: "rgba(231,168,58,0.16)" }}><div className="text-xl font-bold text-[#9A6516] tabular-nums">Fri</div><div className="text-xs text-muted-foreground">Next visit</div></div>
            </div>
            <Separator className="my-4" />
            <div className="text-sm font-semibold text-[#252221] mb-2">Your next steps</div>
            <ul className="space-y-2 text-sm">
              {["Sign the listing agreement", "Upload Mama’s medical summary", "Confirm Friday’s home walkthrough"].map((t, i) => (
                <li key={t} className="flex items-center gap-2"><Checkbox defaultChecked={i === 0} /> <span className={i === 0 ? "line-through text-muted-foreground" : ""}>{t}</span></li>
              ))}
            </ul>
          </Card>

          {/* Meeting room */}
          <Card className="border-[#E6EDF2] overflow-hidden" style={{ boxShadow: "var(--mmm-shadow-sm)" }} data-testid="ds-meeting-room">
            <div className="p-3 grid grid-cols-2 gap-2" style={{ backgroundColor: "#12313f" }}>
              {["Coordinator", "Jane S.", "Realtor", "You"].map((n, i) => (
                <div key={n} className="relative rounded-lg aspect-video flex items-center justify-center" style={{ backgroundColor: i === 3 ? "#1C4358" : "#1a3b4c" }}>
                  <span className="h-10 w-10 rounded-full flex items-center justify-center text-white text-sm font-semibold" style={{ backgroundColor: "rgba(255,255,255,0.14)" }}>{n.split(" ").map((x) => x[0]).join("")}</span>
                  <span className="absolute bottom-1 left-1.5 text-[10px] text-white/85">{n}</span>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-center gap-2 py-3 bg-[#0f2833]">
              <button className="mmm-tap h-11 w-11 rounded-full flex items-center justify-center bg-white/15 text-white hover:bg-white/25 transition-colors" aria-label="Mute"><Mic size={18} /></button>
              <button className="mmm-tap h-11 w-11 rounded-full flex items-center justify-center bg-white/15 text-white hover:bg-white/25 transition-colors" aria-label="Camera"><Video size={18} /></button>
              <button className="mmm-tap h-11 w-11 rounded-full flex items-center justify-center bg-[#9C1E22] text-white hover:bg-[#861A1D] transition-colors" aria-label="Leave"><PhoneOff size={18} /></button>
            </div>
          </Card>
        </div>
      </Section>

      {/* Sticky mobile footer */}
      <Section id="mobile-footer" icon={Smartphone} title="Sticky Mobile Footer"
        desc="On phones, key actions stay one thumb away: Call, Free Consultation, Chat, and Facebook/Login. Shown here inside a device frame.">
        <div className="relative mx-auto w-full max-w-[320px] rounded-[28px] border-[6px] border-[#252221] overflow-hidden bg-white" style={{ height: 440 }} data-testid="ds-mobile-frame">
          <img src={IMG.shore} alt="Peaceful shoreline at sunrise" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 mmm-hero-overlay" />
          <div className="relative p-4 text-white">
            <div className="text-xs font-semibold" style={{ color: "var(--mmm-gold)" }}>MovingMyMama</div>
            <h3 className="mt-1 font-display text-xl font-bold leading-tight">A gentle next step for your family</h3>
            <p className="mt-1.5 text-sm text-white/90">Tap below — we’re one call away.</p>
          </div>
          <StickyMobileFooter variant="static" onChat={() => toast.info("Chat would open here")} />
        </div>
      </Section>
    </div>
  );
}
